import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Using Backbone to improve multiselects",
  "description": "",
  "published": "2012-04-13T00:00:00.000Z",
  "redirect_from": ["/blog/using-backbone-to-improve-multiselects/swizec/4172"],
  "categories": "Checkbox, HTML, JavaScript, jQuery, Languages, Programming, Scripts, Uncategorized, User interface",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Sometimes you need to ask a users for multiple answers to a single question. But what's the best way to go about it?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/04/Screen-Shot-2012-04-13-at-10.43.12-PM.png",
        "alt": "Checkbox-field",
        "title": "Checkbox-field"
      }}></img></p>
    <p>{`A multiselect input `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Field_%28mathematics%29",
        "title": "Field (mathematics)"
      }}>{`field`}</a>{` is kind of strange for the user, sticks out and doesn't really behave like everything else on the web.`}</p>
    <p>{`A bunch of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Checkbox",
        "title": "Checkbox"
      }}>{`checkboxes`}</a>{` is better for the user ... but have you ever tried handling a form with dynamically created checkboxes? Let alone creating useful tests and consolidating everything into a single list of values. No decent forms framework will let you do that easily.`}</p>
    <p>{`I had to solve this similar problem for a `}<a parentName="p" {...{
        "href": "http://www.nextpunch.com/"
      }}>{`time and attendance software`}</a>{` I worked on, so I made a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`Javascript`}</a>{` thing to convert a select field into a `}<a parentName="p" {...{
        "href": "http://swizec.github.com/checkbox-field/"
      }}>{`checkbox-field`}</a>{`. Then I made it into a simple jquery plugin thing for everyone to use :)`}</p>
    <p><a parentName="p" {...{
        "href": "http://swizec.github.com/checkbox-field/"
      }}>{`Check out the demo!`}</a></p>
    <p>{`The idea is pretty simple:`}</p>
    <ul>
      <li parentName="ul">{`take a select, turn it into a list of options`}</li>
      <li parentName="ul">{`every option becomes a `}<em parentName="li">{`Checkbox`}</em>{` model in a collection`}</li>
      <li parentName="ul">{`render each Checkbox as a `}<em parentName="li">{`CheckboxView`}</em></li>
      <li parentName="ul">{`connect everything with some events`}</li>
    </ul>
    <p>{`What I really love about the Backbone approach is that all of this works almost magically. Instead of bending over backwards to get checkboxes and the hidden multiselect synced up, all I had to do was create some models, some views and tell them how they are connected.`}</p>
    <p>{`This is how the field itself is created`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`$multiselect.find("option").each(function (i, el) {
  var $el = $(el);
  // hidden behind the scenes here is that:
  // 1. creating a Checkbox, correctly binds together all needed events
  // 2. adding it to checkboxes, takes care of rendering and adding to the DOM
  checkboxes.add(
    new Checkbox({
      value: $el.val(),
      label: $el.html(),
      selected: $el.attr("selected"),
    })
  );
});
`}</code></pre>
    <p>{`And to make sure data is synced up between its three representations (multiselect, visual checkboxes, the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Data_structure",
        "title": "Data structure"
      }}>{`data structure`}</a>{` itself), all it takes is this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var Checkbox = Backbone.Model.extend({
  initialize: function () {
    this.bind("change:selected", this.toggled);

    this.attributes.id = this.cid;
  },

  toggled: function () {
    var $opt = $multiselect.find("option[value=" + this.get("value") + "]");

    if (this.get("selected")) {
      $opt.attr("selected", "1");
    } else {
      $opt.removeAttr("selected");
    }
  },
});
`}</code></pre>
    <p>{`Yep, that's the whole model and that's all it takes to sync everything up. Pretty cool huh? I remember trying to do this with just `}<a parentName="p" {...{
        "href": "http://jquery.com/",
        "title": "JQuery"
      }}>{`jQuery`}</a>{` once ... I nearly stabbed my eyes out to end the misery.`}</p>
    <p>{`All in all the whole thing is just `}<em parentName="p">{`123 sloc`}</em>{`, which can only mean one thing: Backbone is cool.`}</p>
    <p>{`If you use my `}<a parentName="p" {...{
        "href": "http://swizec.github.com/checkbox-field/"
      }}>{`checkbox-field`}</a>{` library, it's only going to be a single sloc for you:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`$("#my_selector").checkboxField();
`}</code></pre>
    <p>{`That's it :)`}</p>
    <p>{`Let me know what you think.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.ki6i.com/jquery-multiselect-abreviation-list/"
        }}>{`JQuery Multiselect - Abbreviation List`}</a>{` (ki6i.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8877080/retrieving-selected-values-from-wpf-listbox-with-multiselect"
        }}>{`Retrieving selected values from WPF ListBox with multiselect`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9025172/how-can-i-get-element-state-from-appendchild-checkbox-in-javascript"
        }}>{`How can I get element state from appendchild checkbox in Javascript?`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9404605/controlling-checkboxes-in-html"
        }}>{`Controlling Checkboxes in HTML`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9396182/simulate-holding-ctrl-key"
        }}>{`Simulate Holding Ctrl Key`}</a>{` (stackoverflow.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=f7e989e4-2ff3-4685-8e25-afe1c681042a",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      